import { useAction, useAtom } from '@reatom/npm-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { Typography, useHapticFeedback } from '@blade/ui-kit'
import { userAtom } from '@root/entities/auth'
import { UserAvatar } from '@root/entities/auth/ui'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { UserShort } from '@root/entities/user-short'
import { useAppRoutes } from '@root/features/routing'
import { reatomAddWalletDrawerOpenedAtom } from '@root/features/wallet/add-wallet'
import { mediaMinTablet, WalletIcon } from '@root/shared/ui-kit'

import type { ClassNameProps } from '@root/shared/react'

type Props = ClassNameProps
export const SummaryCardOutlet: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation()
  const [user] = useAtom(userAtom)
  const [network] = useAtom(selectedNetworkAtom)
  const { navigateToProfileIndex } = useAppRoutes()
  const haptics = useHapticFeedback()
  const toggleAddWalletDrawer = useAction(
    reatomAddWalletDrawerOpenedAtom.toggle,
  )

  const handleNewWallet: React.MouseEventHandler = (e) => {
    e.stopPropagation()
    haptics.impactOccurred('light')
    toggleAddWalletDrawer()
  }

  return (
    <Root
      className={className}
      onClick={(e) => {
        e.stopPropagation()
        haptics.impactOccurred('light')
        navigateToProfileIndex()
      }}
    >
      <UserAvatar />
      <Name ellipsis>{user ? UserShort.someName(user) : '-'}</Name>

      <Button type="button" $network={network} onClick={handleNewWallet}>
        <WalletIcon size={20} />
        <span>{t('wallet.add_wallet')}</span>
      </Button>
    </Root>
  )
}

const Name = styled(Typography).attrs({
  variant: 'body2-medium',
})`
  text-wrap: balance;

  ${mediaMinTablet(css`
    font-size: var(--typography-body-1-medium-font-size);
    line-height: var(--typography-body-1-medium-line-height);
    font-weight: var(--typography-body-1-medium-font-weight);
  `)}
`

const Root = styled.div`
  background: var(--appearance-1);
  min-height: 56px;
  border-radius: 16px;

  display: grid;
  grid-template-columns: auto minmax(0, 1fr) minmax(0, auto);
  align-items: center;
  gap: 8px;
  padding: 4px;
  padding-left: 8px;
`

const GRADIENT_NETWORK_MAP: Record<Network.Network, string> = {
  [Network.Ethereum]: `radial-gradient(120.87% 289.58% at -13.04% -45.83%, #5F97FF 0%, #003699 100%)`,
  [Network.Bsc]: `radial-gradient(120.87% 289.58% at -13.04% -45.83%, #5F97FF 0%, #003699 100%)`,
  [Network.Base]: `radial-gradient(120.87% 289.58% at -13.04% -45.83%, #5F97FF 0%, #003699 100%)`,
  [Network.Solana]: `radial-gradient(224.21% 135.02% at 3.23% 0%, #A781FF 0%, #7337FF 100%)`,
  [Network.Ton]: `radial-gradient(301.86% 237.26% at -13.04% -45.83%, #0098E9 0%, #006397 100%)`,
}

const Button = styled.button<{ $network: Network.Network }>`
  cursor: pointer;
  background-image: ${({ $network }) => GRADIENT_NETWORK_MAP[$network]};
  border: none;
  outline: none;
  align-self: stretch;
  border-radius: 12px;
  color: var(--text-12);
  transition: all 0.15s linear;
  padding-inline: 16px;
  font-size: var(--typography-body-2-bold-font-size);
  line-height: var(--typography-body-2-bold-line-height);
  font-weight: var(--typography-body-2-bold-font-weight);
  display: flex;
  align-items: center;
  gap: 4px;

  ${mediaMinTablet(css`
    padding-inline: 40px;
  `)}
`
